/**
 * Utilities for translating enum values to user-friendly display names
 */

import { LoanApplicationSubmitSource2 } from '@servicetitan/financing-api-client';
import type {
    ResidenceOwnership,
    ResidenceType,
    EmploymentStatus,
    Citizenship,
    SpokenLanguage,
} from '@servicetitan/financing-api-client';

/**
 * Generic type for enum translation dictionaries
 */
export type EnumTranslations<T extends string | number> = Partial<Record<T, string>>;

/**
 * Generic function to get translated enum value with fallback
 */
export function translateEnum<T extends string | number>(
    enumValue: T,
    translations: EnumTranslations<T>,
    fallback?: string,
): string {
    return translations[enumValue] ?? fallback ?? String(enumValue);
}

/**
 * Residence ownership translations
 */
export const residenceOwnershipTranslations: EnumTranslations<ResidenceOwnership> = {
    notSet: 'Not specified',
    own: 'Own outright',
    ownWithMortgage: 'Own with mortgage',
    rent: 'Rent',
    other: 'Other',
} as const;

/**
 * Residence type translations
 */
export const residenceTypeTranslations: EnumTranslations<ResidenceType> = {
    notSet: 'Not specified',
    primary: 'Primary residence',
    secondary: 'Secondary or vacation home',
    investment: 'Investment property',
} as const;

/**
 * Employment status translations
 */
export const employmentStatusTranslations: EnumTranslations<EmploymentStatus> = {
    notSet: 'Not specified',
    employed: 'Employed',
    retired: 'Retired',
    notEmployed: 'Not employed',
    other: 'Other',
} as const;

/**
 * Citizenship status translations
 */
export const citizenshipTranslations: EnumTranslations<Citizenship> = {
    notSet: 'Not specified',
    usCitizen: 'U.S. citizen',
    lawfulPermanentResident: 'Lawful permanent resident',
    other: 'Other',
} as const;

/**
 * Spoken language translations
 */
export const spokenLanguageTranslations: EnumTranslations<SpokenLanguage> = {
    notSet: 'Not specified',
    english: 'English',
    spanish: 'Spanish',
    other: 'Other',
} as const;

/**
 * Application source translations
 */
export const applicationSourceTranslations: EnumTranslations<LoanApplicationSubmitSource2> = {
    [LoanApplicationSubmitSource2.Unknown]: 'Unknown',
    [LoanApplicationSubmitSource2.Mobile]: 'Mobile',
    [LoanApplicationSubmitSource2.OnlineEstimates]: 'Online Estimates',
    [LoanApplicationSubmitSource2.Estimate]: 'Estimate',
    [LoanApplicationSubmitSource2.Project]: 'Project',
    [LoanApplicationSubmitSource2.Invoice]: 'Invoice',
    [LoanApplicationSubmitSource2.PrequalificationLink]: 'Pre-qualification Link',
    [LoanApplicationSubmitSource2.Imported]: 'Imported',
} as const;

// Splits camelCase / snake_case into title-cased words, e.g. `onlineEstimates` -> `Online Estimates`.
const humanizeEnumValue = (value: string): string =>
    value
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, character => character.toUpperCase());

/**
 * Display text for an application's submit source. Also the value stored in the
 * Application Management grid, so cells, column-filter dropdowns, sorting and
 * exports all read the same label.
 */
export const getApplicationSourceDisplayText = (
    source: LoanApplicationSubmitSource2 | string | null | undefined,
): string => {
    if (!source) {
        return applicationSourceTranslations[LoanApplicationSubmitSource2.Unknown]!;
    }
    return translateEnum(
        source as LoanApplicationSubmitSource2,
        applicationSourceTranslations,
        humanizeEnumValue(source),
    );
};

// Letters and digits only, so punctuation in a label can't make two spellings of the same
// source differ ('Pre-qualification Link' vs 'prequalificationLink' vs 'Pre Qualification Link').
const canonicalSourceKey = (source: LoanApplicationSubmitSource2 | string | null | undefined) =>
    getApplicationSourceDisplayText(source)
        .replace(/[^a-z0-9]/gi, '')
        .toLowerCase();

/**
 * Compares an application's submit source against selected filter values by display
 * text, so a human-readable selection matches the underlying enum value and vice versa.
 */
export const applicationSourceMatchesFilter = (
    source: LoanApplicationSubmitSource2 | string | null | undefined,
    selectedSources: (LoanApplicationSubmitSource2 | string)[],
): boolean => {
    const sourceKey = canonicalSourceKey(source);
    return selectedSources.some(selected => canonicalSourceKey(selected) === sourceKey);
};

/**
 * Static class for enum translations with organized methods
 */
export class Translate {
    /**
     * Translate residence ownership values
     */
    static residenceOwnership(value: ResidenceOwnership): string {
        return translateEnum(value, residenceOwnershipTranslations);
    }

    /**
     * Translate residence type values
     */
    static residenceType(value: ResidenceType): string {
        return translateEnum(value, residenceTypeTranslations);
    }

    /**
     * Translate employment status values
     */
    static employmentStatus(value: EmploymentStatus): string {
        return translateEnum(value, employmentStatusTranslations);
    }

    /**
     * Translate citizenship status values
     */
    static citizenship(value: Citizenship): string {
        return translateEnum(value, citizenshipTranslations);
    }

    /**
     * Translate spoken language values
     */
    static spokenLanguage(value: SpokenLanguage): string {
        return translateEnum(value, spokenLanguageTranslations);
    }

    /**
     * Translate application submit source values
     */
    static applicationSource(value: LoanApplicationSubmitSource2 | string | null): string {
        return getApplicationSourceDisplayText(value);
    }
}
